/* /biometric/devices/:id — Overview (values for re-setup, rotate token), Punches, Commands, Troubleshooting. */
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAllEmployees, useClock, useConnectionInfo, useDeleteDevice, useDevices, usePunches, useRotateDeviceToken, useSyncDevice, useTestDevice } from "../../data/hooks";
import { Alert, Button, Card, Chip, Confirm, EmptyState, ErrorBox, Grid, Kv, Loaded, PageHeader, Skeleton, Tabs, errMsg, useToast } from "../../ui";
import { useI18n } from "../../i18n";
import { ago } from "../../lib/format";
import { CommandsPanel } from "./components/CommandsPanel";
import { DeviceStatus, EditDeviceModal } from "./components/DeviceBits";
import { FileImport } from "./components/FileImport";
import { Troubleshooting } from "./components/Troubleshooting";
import { ValuesPanel, isMasked } from "./components/ValuesPanel";
import { canDemo, useDemoPunch } from "./components/demo";
import { directionLabel, hm, protocolLabel, verifyLabel } from "./components/labels";
import { syncToast } from "./Biometric";
import "./time.css";

type Tab = "overview" | "punches" | "commands" | "troubleshooting";
const TABS: Tab[] = ["overview", "punches", "commands", "troubleshooting"];

export default function DeviceDetail() {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const i = useI18n();
  const { L, t, lang } = i;
  const toast = useToast();
  const nav = useNavigate();
  const { today, now } = useClock();
  const devs = useDevices();
  const info = useConnectionInfo();
  const emps = useAllEmployees();
  const rotate = useRotateDeviceToken();
  const test = useTestDevice();
  const sync = useSyncDevice();
  const del = useDeleteDevice();
  const demo = useDemoPunch();
  const [token, setToken] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [edit, setEdit] = useState(false);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const tabQ = sp.get("tab") as Tab | null;
  const tab: Tab = tabQ && TABS.includes(tabQ) ? tabQ : "overview";
  const punches = usePunches({ deviceId: id, page, pageSize: 25 }, { enabled: tab === "punches" });
  const back = { to: "/biometric", label: L("Biometric devices", "أجهزة البصمة") };

  if (devs.error) return <><PageHeader title={L("Device", "الجهاز")} back={back} /><ErrorBox error={devs.error} onRetry={() => devs.refetch()} /></>;
  if (!devs.data) return <Skeleton />;
  const d = devs.data.find((x) => x.id === id);
  if (!d) return <><PageHeader title={L("Device not found", "الجهاز غير موجود")} back={back} /><EmptyState title={L("This device doesn't exist or was deleted", "هذا الجهاز غير موجود أو تم حذفه")} action={<Link className="btn pri" to="/biometric">{L("Back to devices", "العودة إلى الأجهزة")}</Link>} /></>;

  const empName = (eid: number | null) => { const e = eid == null ? undefined : emps.data?.find((x) => x.id === eid); return e ? i.name(e) : null; };
  const doRotate = () => rotate.mutate(d.id, {
    onSuccess: (r) => { setToken(r.webhookToken); setConfirmRotate(false); toast(L("New token created — update it on the device", "تم إنشاء رمز جديد — حدّثه في الجهاز")); },
    onError: (e) => toast(errMsg(e), "bad"),
  });
  const doTest = () => test.mutate(d.id, { onSuccess: (r) => toast(r.message, r.ok ? "ok" : "bad"), onError: (e) => toast(errMsg(e), "bad") });
  const doSync = () => sync.mutate(d.id, { onSuccess: (r) => toast(syncToast(d, r.accepted, L)), onError: (e) => toast(errMsg(e), "bad") });
  const doDelete = () => del.mutate(d.id, { onSuccess: () => { toast(L("Device removed — its punches are kept", "تم حذف الجهاز — البصمات محفوظة")); nav("/biometric"); }, onError: (e) => toast(errMsg(e), "bad") });
  const sendDemo = async () => {
    setBusy(true);
    try {
      const r = await demo.send(d, token);
      const n = r.employee ? i.name(r.employee) : null;
      toast(n ? L(`Test punch received for ${n} — attendance updated`, `وصلت بصمة تجريبية لـ ${n} — تم تحديث الحضور`) : L("Test punch received", "وصلت البصمة التجريبية"));
    } catch (e) { toast(errMsg(e), "bad"); } finally { setBusy(false); }
  };
  const setTab = (k: Tab) => { const n = new URLSearchParams(sp); if (k === "overview") n.delete("tab"); else n.set("tab", k); setSp(n, { replace: true }); };
  const hasToken = d.protocol === "webhook" || d.protocol === "agent";

  let body: React.ReactNode = null;
  if (tab === "overview") {
    body = (
      <Grid cols="2">
        <Card title={L("Device details", "بيانات الجهاز")}>
          <Kv items={[
            [L("Serial number", "الرقم التسلسلي"), <code key="s" dir="ltr">{d.serial}</code>],
            [L("Brand · model", "العلامة · الطراز"), `${d.vendor} · ${d.model || "—"}`],
            [L("Method", "الطريقة"), protocolLabel(d.protocol, L)],
            [t("Branch"), d.branch ? i.branch(d.branch) : "—"],
            [L("Firmware", "البرنامج الثابت"), d.firmware || "—"],
            [L("IP address", "عنوان IP"), <span key="ip" dir="ltr" className="num">{d.ip || "—"}</span>],
            [L("Last seen", "آخر ظهور"), d.lastSeen ? `${d.lastSeen.slice(0, 10)} ${hm(d.lastSeen)} · ${ago(d.lastSeen, now, lang)}` : L("Never", "لم يتصل بعد")],
            [L("Last punch", "آخر بصمة"), d.lastPunchAt ? `${d.lastPunchAt.slice(0, 10)} ${hm(d.lastPunchAt)}` : "—"],
            [L("Time zone", "المنطقة الزمنية"), d.timezone],
            [L("Users · punches", "المستخدمون · البصمات"), `${d.userCount} · ${d.punchCount}`],
          ]} />
          {demo.enabled && (
            <div className="mt stack" style={{ gap: 6 }}>
              {canDemo(d, token)
                ? <div><Button variant="sm" icon="play" onClick={sendDemo} loading={busy} data-testid="btn-demo-punch">{L("Send a test punch (demo)", "إرسال بصمة تجريبية (عرض)")}</Button></div>
                : hasToken && <span className="xs muted" data-testid="demo-hint">{L("Demo: rotate the token to send a test punch.", "للعرض: جدّد الرمز لإرسال بصمة تجريبية.")}</span>}
            </div>
          )}
        </Card>
        <Card title={L("Setup values", "قيم الإعداد")} sub={L("Use these if you reset or replace the device", "استخدمها عند إعادة ضبط الجهاز أو استبداله")}
          actions={hasToken ? <Button variant="sm" icon="refresh" onClick={() => setConfirmRotate(true)} data-testid="btn-rotate">{L("Rotate token", "تجديد الرمز")}</Button> : undefined}>
          {info.data ? <ValuesPanel device={d} info={info.data} token={token ?? d.webhookToken} /> : info.error ? <ErrorBox error={info.error} /> : <Skeleton />}
          {token && !isMasked(token) && <div className="mt"><Button variant="ghost sm" onClick={() => setToken(null)} data-testid="btn-hide-token">{L("I've copied it — hide", "نسخته — إخفاء")}</Button></div>}
          {d.protocol === "file" && <div className="mt"><FileImport deviceId={d.id} /></div>}
        </Card>
      </Grid>
    );
  } else if (tab === "punches") {
    body = (
      <Card flush testId="device-punches">
        <Loaded q={punches}>{(pg) => pg.items.length === 0 ? <EmptyState title={L("No punches from this device yet", "لا توجد بصمات من هذا الجهاز بعد")} /> : (
          <>
            <div className="tw"><table className="tbl cards compact">
              <thead><tr><th>{L("Time", "الوقت")}</th><th>{L("Device user", "مستخدم الجهاز")}</th><th>{t("Employee")}</th><th>{L("Direction", "الاتجاه")}</th><th className="hide-md">{L("Verified by", "طريقة التحقق")}</th></tr></thead>
              <tbody>{pg.items.map((p) => (
                <tr key={p.id}>
                  <td className="k1 num">{p.time.slice(0, 10)} <b>{hm(p.time)}</b></td>
                  <td className="x num" data-l={L("Device user", "مستخدم الجهاز")}>{p.deviceUserId}</td>
                  <td className="k2">{empName(p.employeeId) ?? <Link to="/biometric/unmatched"><Chip tone="warn">{L("Unmatched", "غير مرتبط")}</Chip></Link>}</td>
                  <td className="x" data-l={L("Direction", "الاتجاه")}>{directionLabel(p.direction, L)}</td>
                  <td className="hide-md sm muted">{verifyLabel(p.verify, L)}</td>
                </tr>
              ))}</tbody>
            </table></div>
            <Pager page={pg.page} size={pg.pageSize} total={pg.total} onPage={setPage} />
          </>
        )}</Loaded>
      </Card>
    );
  } else if (tab === "commands") {
    body = <Card><CommandsPanel device={d} today={today} /></Card>;
  } else {
    body = <Card title={L("Troubleshooting", "حل المشكلات")} sub={L("Most problems are fixed on the device in a minute", "تُحل معظم المشكلات من الجهاز خلال دقيقة")}><Troubleshooting /></Card>;
  }

  return (
    <div data-testid="device-detail">
      <PageHeader back={back} title={d.name} sub={<span className="row" style={{ gap: 8, display: "inline-flex" }}><DeviceStatus d={d} now={now} /><span>{protocolLabel(d.protocol, L)}</span></span>}
        actions={<>
          {(d.protocol === "push" || d.protocol === "poll" || d.protocol === "agent") && <Button icon="refresh" onClick={doSync} loading={sync.isPending} data-testid="btn-sync">{L("Sync now", "مزامنة الآن")}</Button>}
          <Button icon="wifi" onClick={doTest} loading={test.isPending} data-testid="btn-test">{L("Test", "اختبار")}</Button>
          <Button icon="edit" onClick={() => setEdit(true)} data-testid="btn-edit">{t("Edit")}</Button>
          <Button variant="danger" icon="trash" onClick={() => setConfirmDel(true)} data-testid="btn-delete">{L("Delete", "حذف")}</Button>
        </>} />
      {d.status === "offline" && (
        <div className="mb"><Alert tone="bad" testId="offline-alert" title={L("This device is offline", "هذا الجهاز غير متصل")}
          desc={L(`No contact since ${d.lastSeen ? ago(d.lastSeen, now, "en") : "setup"}. Punches are stored on the device and arrive when it reconnects.`, `لا يوجد اتصال منذ ${d.lastSeen ? ago(d.lastSeen, now, "ar") : "الإعداد"}. تُحفظ البصمات في الجهاز وتصل عند عودة الاتصال.`)}
          action={<Button variant="sm" onClick={() => setTab("troubleshooting")}>{L("Troubleshoot", "حل المشكلة")}</Button>} /></div>
      )}
      <section className="card">
        <Tabs<Tab> value={tab} onChange={setTab} label={L("Device sections", "أقسام الجهاز")}
          items={[["overview", L("Overview", "نظرة عامة")], ["punches", L("Punches", "البصمات")], ["commands", L("Commands", "الأوامر")], ["troubleshooting", L("Troubleshooting", "حل المشكلات")]]} />
      </section>
      <div className="mt">{body}</div>

      {edit && <EditDeviceModal device={d} onClose={() => setEdit(false)} />}
      {confirmRotate && <Confirm title={L("Rotate the device token?", "تجديد رمز الجهاز؟")} confirmLabel={L("Rotate token", "تجديد الرمز")} busy={rotate.isPending} onClose={() => setConfirmRotate(false)} onConfirm={doRotate}
        body={L("The old token stops working immediately. Paste the new one into the device or agent.", "يتوقف الرمز القديم فورًا. الصق الرمز الجديد في الجهاز أو الوكيل.")} />}
      {confirmDel && <Confirm title={L("Delete this device?", "حذف هذا الجهاز؟")} danger busy={del.isPending} confirmLabel={L("Delete device", "حذف الجهاز")} onClose={() => setConfirmDel(false)} onConfirm={doDelete}
        body={L(`“${d.name}” will stop sending punches to StartHR. Punches already received stay in attendance.`, `سيتوقف «${d.name}» عن إرسال البصمات إلى ستارت HR. البصمات المستلمة تبقى في الحضور.`)} />}
    </div>
  );
}

export function Pager({ page, size, total, onPage }: { page: number; size: number; total: number; onPage: (p: number) => void }) {
  const { L } = useI18n();
  const pages = Math.max(1, Math.ceil(total / size));
  if (total <= size) return <div className="card-b xs muted" data-testid="pager-total">{L(`${total} records`, `${total} سجل`)}</div>;
  return (
    <div className="card-b row between" data-testid="pager">
      <span className="xs muted">{L(`${(page - 1) * size + 1}–${Math.min(total, page * size)} of ${total}`, `${(page - 1) * size + 1}–${Math.min(total, page * size)} من ${total}`)}</span>
      <div className="row" style={{ gap: 6 }}>
        <Button variant="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="page-prev">{L("Previous", "السابق")}</Button>
        <Button variant="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="page-next">{L("Next", "التالي")}</Button>
      </div>
    </div>
  );
}
